<template>
  <div class="csc-outer">
    <canvas ref="canvas"></canvas>
    <div v-if="isEmpty" class="csc-empty">No expense data</div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { markRaw } from 'vue';
Chart.register(...registerables);

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function daysIn(year, month) { return new Date(year, month + 1, 0).getDate(); }

function dailyTotals(expenses, year, month) {
  const days = daysIn(year, month);
  const out  = new Array(days).fill(0);
  expenses.forEach(t => {
    if (!t.v3_date) return;
    const d = parseInt(t.v3_date.split('-')[2], 10) - 1;
    if (d >= 0 && d < days) out[d] += Math.abs(t.v3_amount || 0);
  });
  return out;
}

function cumulative(arr, cutoff) {
  let sum = 0;
  return arr.map((v, i) => {
    if (cutoff != null && i > cutoff) return null;
    sum += (v || 0);
    return Math.round(sum);
  });
}

function pad(arr, len) {
  const out = [...arr];
  while (out.length < len) out.push(null);
  return out;
}

function mlabel(y, m) { return MONTH_NAMES[m] + ' ' + y; }

export default {
  name: 'CumulativeSpendChart',
  props: {
    expenses:              { type: Array,  default: () => [] },
    selectedYear:          { type: Number, required: true },
    selectedMonth:         { type: Number, required: true },
    prevMonthExpenses:     { type: Array,  default: () => [] },
    prevMonthYear:         { type: Number, default: null },
    prevMonth:             { type: Number, default: null },
    prevPrevMonthExpenses: { type: Array,  default: () => [] },
    prevPrevMonthYear:     { type: Number, default: null },
    prevPrevMonth:         { type: Number, default: null },
    totalBudget:           { type: Number, default: 0 },
  },
  data() { return { chart: null, renderTimer: null }; },
  computed: {
    isEmpty() { return this.expenses.length === 0; },
    days()    { return daysIn(this.selectedYear, this.selectedMonth); },
    todayIdx() {
      const n = new Date();
      return n.getMonth() === this.selectedMonth && n.getFullYear() === this.selectedYear
        ? n.getDate() - 1 : null;
    },
  },
  methods: {
    scheduleRender() {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => { this.renderTimer = null; this.build(); }, 150);
    },
    _buildDatasets(days, today, labels) {
      const datasets = [];

      if (this.totalBudget > 0) {
        datasets.push({
          label: 'Budget pace',
          data: labels.map(d => days <= 1
            ? this.totalBudget
            : Math.round(this.totalBudget / 2 + (this.totalBudget / 2) * (d - 1) / (days - 1))
          ),
          borderColor: 'rgba(107,114,128,0.45)',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [6, 5],
          pointRadius: 0,
          tension: 0,
          order: 10,
        });
      }

      if (this.prevPrevMonth != null && this.prevPrevMonthExpenses.length > 0) {
        datasets.push({
          label: mlabel(this.prevPrevMonthYear, this.prevPrevMonth),
          data: pad(cumulative(dailyTotals(this.prevPrevMonthExpenses, this.prevPrevMonthYear, this.prevPrevMonth)), days),
          borderColor: 'rgba(156,163,175,0.65)',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [4, 4],
          pointRadius: 0,
          tension: 0.15,
          order: 3,
          spanGaps: true,
        });
      }

      if (this.prevMonth != null && this.prevMonthExpenses.length > 0) {
        datasets.push({
          label: mlabel(this.prevMonthYear, this.prevMonth),
          data: pad(cumulative(dailyTotals(this.prevMonthExpenses, this.prevMonthYear, this.prevMonth)), days),
          borderColor: 'rgba(251,146,60,0.8)',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.15,
          order: 2,
          spanGaps: true,
        });
      }

      datasets.push({
        label: mlabel(this.selectedYear, this.selectedMonth),
        data: pad(cumulative(dailyTotals(this.expenses, this.selectedYear, this.selectedMonth), today), days),
        borderColor: 'rgba(79,142,247,1)',
        backgroundColor: 'rgba(79,142,247,0.07)',
        fill: true,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.15,
        order: 1,
        spanGaps: false,
      });

      return datasets;
    },
    build() {
      const canvas = this.$refs.canvas;
      if (!canvas || canvas.offsetWidth === 0) { this.scheduleRender(); return; }
      if (this.isEmpty) return;

      if (this.chart) {
        // Update data in place — no destroy/recreate, no rAF race
        const days   = this.days;
        const today  = this.todayIdx;
        const labels = Array.from({ length: days }, (_, i) => i + 1);
        this.chart.data.labels   = labels;
        this.chart.data.datasets = this._buildDatasets(days, today, labels);
        this.chart.update('none');
        return;
      }

      if (!canvas.getContext('2d')) { this.scheduleRender(); return; }
      this._create(canvas);
    },
    _create(canvas) {
      if (this.chart || !this.$refs.canvas) return;

      const days   = this.days;
      const today  = this.todayIdx;
      const labels = Array.from({ length: days }, (_, i) => i + 1);

      // Arrow function so the plugin always reads this.todayIdx (current, not stale)
      const todayPlugin = {
        id: 'todayLine',
        afterDraw: (chart) => {
          const t = this.todayIdx;
          if (t == null) return;
          const last = chart.data.datasets.length - 1;
          const pt   = chart.getDatasetMeta(last)?.data?.[t];
          if (!pt) return;
          const { ctx, chartArea: { top, bottom } } = chart;
          ctx.save();
          ctx.setLineDash([3, 3]);
          ctx.strokeStyle = 'rgba(79,142,247,0.35)';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(pt.x, top);
          ctx.lineTo(pt.x, bottom);
          ctx.stroke();
          ctx.restore();
        },
      };

      this.chart = markRaw(new Chart(canvas, {
        type: 'line',
        data: { labels, datasets: this._buildDatasets(days, today, labels) },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true,
                padding: 14,
                font: { size: 12 },
                filter: item => item.text !== 'Budget pace' || this.totalBudget > 0,
              },
            },
            tooltip: {
              callbacks: {
                title: items => `Day ${items[0].label}`,
                label: ctx => {
                  const v = ctx.parsed.y;
                  if (v == null) return null;
                  const over = ctx.dataset.label === mlabel(this.selectedYear, this.selectedMonth)
                    && this.totalBudget > 0
                    ? (() => {
                      const pace = Math.round(this.totalBudget * (ctx.dataIndex + 1) / this.days);
                      const diff = Math.round(v) - pace;
                      return diff > 0 ? `  (+$${diff.toLocaleString()} over pace)` : '';
                    })() : '';
                  return ` ${ctx.dataset.label}: $${Math.round(v).toLocaleString()}${over}`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: {
                color: '#9ca3af', font: { size: 11 }, maxRotation: 0,
                callback: (_, i) => { const d = i + 1; return d === 1 || d % 5 === 0 ? d : ''; },
              },
            },
            y: {
              border: { display: false },
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: {
                color: '#9ca3af', font: { size: 11 },
                callback: v => '$' + Math.round(v).toLocaleString(),
              },
            },
          },
        },
        plugins: [todayPlugin],
      }));
    },
  },
  watch: {
    expenses:              { handler() { this.scheduleRender(); }, deep: true },
    prevMonthExpenses:     { handler() { this.scheduleRender(); }, deep: true },
    prevPrevMonthExpenses: { handler() { this.scheduleRender(); }, deep: true },
    totalBudget()          { this.scheduleRender(); },
  },
  mounted() { this.scheduleRender(); },
  beforeUnmount() {
    if (this.renderTimer) clearTimeout(this.renderTimer);
    if (this.chart) { try { this.chart.destroy(); } catch (e) { /* ignore */ } this.chart = null; }
  },
};
</script>

<style scoped>
.csc-outer {
  position: relative;
  height: 260px;
  width: 100%;
}
.csc-empty {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; color: #9ca3af;
}
@media (max-width: 600px) { .csc-outer { height: 210px; } }
</style>
